import { useTranslation } from 'react-i18next';
import TextButton from '@/components/@IntranetLibrary/Button/TextButton';
import Select from '@/components/@IntranetLibrary/Select';
import {
  ApprovalClearableInput,
  approvalOutlineSelectSx,
} from '@/components/Approval/@components/ApprovalFormTable';
import {
  ExpenseFieldLabel,
  ExpenseVendorCard,
} from '@/components/Approval/@components/ExpensePreApproval';
import {
  PAY_TYPE_OPTIONS,
  PERIOD_TYPE_OPTIONS,
  type ExpenseDetailRow,
  type ExpenseVendor,
  type PayType,
  type PeriodType,
} from '@/utils/approval/expensePreApproval';
import ItemTable, { type ItemTableChangeKind } from './ItemTable';
import { VendorDeleteSlot } from './styles';

type VendorBlockProps = {
  vendor: ExpenseVendor;
  showPeriod: boolean;
  canDelete: boolean;
  onChange: (next: ExpenseVendor, kind: ItemTableChangeKind) => void;
  onDelete: () => void;
};

const RequiredLabel = ({ text }: { text: string }) => (
  <ExpenseFieldLabel>
    {text}
    <span className="required">*</span>
  </ExpenseFieldLabel>
);

/** 업체 블록 — 업체명·헤더적요·지급방식·(지급용역비면 단위)·품목 표 */
const VendorBlock = ({
  vendor,
  showPeriod,
  canDelete,
  onChange,
  onDelete,
}: VendorBlockProps) => {
  const { t } = useTranslation();
  const edit = (next: Partial<ExpenseVendor>) =>
    onChange({ ...vendor, ...next }, 'edit');
  const handleRows = (details: ExpenseDetailRow[], kind: ItemTableChangeKind) =>
    onChange({ ...vendor, details }, kind);

  return (
    <ExpenseVendorCard className="expense-info-item" data-vendor-id={vendor.id}>
      <VendorDeleteSlot>
        <TextButton variant="caution" disabled={!canDelete} onClick={onDelete}>
          {t('삭제')}
        </TextButton>
      </VendorDeleteSlot>
      <div className="field">
        <RequiredLabel text={t('업체명')} />
        <ApprovalClearableInput
          name="vendor"
          placeholder={t('업체명을 입력해 주세요 (최대 100자)')}
          value={vendor.vendor}
          onChange={(next) => edit({ vendor: next })}
        />
      </div>
      <div className="field">
        <RequiredLabel text={t('헤더적요')} />
        <ApprovalClearableInput
          name="summary"
          placeholder={t('요약한 내용을 입력해 주세요 (최대 100자)')}
          value={vendor.summary}
          onChange={(next) => edit({ summary: next })}
        />
      </div>
      <div className="field">
        <RequiredLabel text={t('지급방식')} />
        <Select
          fullWidth
          value={vendor.payType}
          options={PAY_TYPE_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.label),
          }))}
          onChange={(e) => edit({ payType: e.target.value as PayType })}
          sx={approvalOutlineSelectSx}
        />
      </div>
      {showPeriod && (
        <div className="field">
          <RequiredLabel text={t('단위')} />
          <Select
            fullWidth
            value={vendor.periodType}
            options={PERIOD_TYPE_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.label),
            }))}
            onChange={(e) => edit({ periodType: e.target.value as PeriodType })}
            sx={approvalOutlineSelectSx}
          />
        </div>
      )}
      <ItemTable rows={vendor.details} onChange={handleRows} />
    </ExpenseVendorCard>
  );
};

export default VendorBlock;
